import './index.scss';

import { Component, createRef, KeyboardEvent, ReactNode } from 'react';

import strings from '../../../utils/strings';
import { UncontrolledField } from '..';
import { BraxListItem } from '../../BraxList';
import { BraxDropdown, BraxDropdownProps } from '../../BraxDropdown';
import { BraxIcon } from '../../BraxIcon';

export type BraxSelectValue = { id: Exclude<BraxSelectValue, object> } | string | number | boolean | undefined | null;

export type BraxSelectOption<V extends BraxSelectValue> = Omit<BraxListItem, 'onSelect' | 'link' | 'hidden'> & {
    value: V,
    search?: string
};

export type BraxSelectVariant = 'simple' | 'no-description' | 'complete' | 'complete-value-as-title' | 'label-as-title' | 'arrow-on-label';

export type BraxUncontrolledSelectFieldProps<V extends BraxSelectValue> = UncontrolledField<V> & Partial<Pick<BraxDropdownProps, 'position'>> & {
    placeholder?: string,
    hint?: ReactNode,
    options: BraxSelectOption<V>[],
    required?: boolean,
    onChange?: (value: V) => void
} & ({
    label: ReactNode,
    variant?: Extract<BraxSelectVariant, 'label-as-title' | 'arrow-on-label'>
} | {
    label?: ReactNode,
    variant?: Exclude<BraxSelectVariant, 'label-as-title' | 'arrow-on-label'>
});

type BraxUncontrolledSelectFieldState = {
    options: BraxListItem[],
    focused: number,
    open: boolean
};

export class BraxUncontrolledSelectField<V extends BraxSelectValue> extends Component<BraxUncontrolledSelectFieldProps<V>, BraxUncontrolledSelectFieldState> {

    static defaultProps = {
        placeholder: '',
        disabled: false,
        status: null,
        variant: 'complete'
    };

    state: BraxUncontrolledSelectFieldState = {
        options: [],
        focused: 0,
        open: false
    };

    ref = createRef<HTMLDivElement>()

    search = '';

    searchTimeout;

    componentDidMount() {
        this.updateOptions();

        window.addEventListener('keydown', event => this.preventScroll(event));
    }

    componentDidUpdate(prevProps: BraxUncontrolledSelectFieldProps<V>) {
        if (prevProps.options !== this.props.options)
            this.updateOptions();
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', event => this.preventScroll(event));
    }

    preventScroll(event: globalThis.KeyboardEvent) {
        if (this.state.open && ['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].indexOf(event.code) > -1)
            event.preventDefault();
    }

    handleKeyup(event: KeyboardEvent<HTMLDivElement>) {
        if (!this.state.open && event.code !== 'Enter')
            return;

        switch (event.code) {
            case 'Enter':
            case 'Space':
                this.props.onChange?.(this.props.options[this.state.focused].value);
                break;

            case 'ArrowUp':
                this.setState(prevState => {
                    const focused = Math.max(prevState.focused - 1, 0)

                    this.scrollToOption(focused, true);

                    return { focused };
                });
                break;

            case 'ArrowDown':
                this.setState(prevState => {
                    const focused = Math.min(prevState.focused + 1, this.props.options.length - 1)

                    this.scrollToOption(focused, true);

                    return { focused };
                });
                break;

            default:
                if (event.key.length === 1) {
                    if (this.searchTimeout)
                        clearTimeout(this.searchTimeout);

                    this.searchTimeout = setTimeout(() => this.search = '', 1000);

                    this.search += strings.normalize(event.key);

                    for (let i = 0; i < this.props.options.length; i++) {
                        const { search, title, value } = this.props.options[i];

                        const optionFilter = search || (typeof title === 'string' && title) || (typeof value === 'string' && value) || ''

                        if (strings.normalize(optionFilter).startsWith(this.search)) {
                            this.setState({ focused: i });

                            this.scrollToOption(i, false);

                            break;
                        }
                    }
                }
        }
    }

    scrollToOption(index: number, ifNeeded: boolean) {
        const element = this.ref.current?.querySelector(`.brax-dropdown .brax-list > li:nth-child(${index + 1})`)

        if (ifNeeded)
            (element as any)?.scrollIntoViewIfNeeded(false);
        else
            element?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start'
            });
    }

    updateOptions() {
        const options: BraxListItem[] = [];

        for (const { value, ...option } of this.props.options)
            if (!this.search || strings.normalize(`${option.title}`).includes(this.search))
                options.push({
                    ...option,
                    onSelect: () => this.props.onChange?.(value)
                });

        this.setState({ options });
    }

    is(...options: BraxSelectVariant[]) {
        for (const option of options)
            if (option === this.props.variant)
                return true;

        return false;
    }

    render() {
        const { label, placeholder, status, hint, required, value, onChange, options, variant, ...props } = this.props;

        const selected = options.find(option => typeof option.value === 'object' && typeof value === 'object' ? option.value?.id === value?.id : option.value === value);

        return (
            <div
                ref={this.ref}
                className={`brax-select ${status || ''} ${this.is('label-as-title', 'arrow-on-label') ? variant : ''}`}
                onKeyUp={event => this.handleKeyup(event)}
            >
                <input
                    type="hidden"
                    value={value ? String(value) : ''}
                    {...props}
                />

                <BraxDropdown
                    items={this.state.options}
                    disabled={status === 'disabled'}
                    readOnly={status === 'read-only'}
                    position={this.props.position}
                    listItemProps={(_, index) => ({ className: this.state.focused === index ? 'focused' : '' })}
                    onToggle={open => this.setState({ open })}
                >
                    { !this.is('label-as-title') && label ? (
                        <label>
                            <span>{ label } { required ? '*' : null }</span>

                            { this.is('arrow-on-label') ? (
                                <BraxIcon icon="arrow" />
                            ) : null }
                        </label>
                    ) : null }

                    <span>
                        { this.is('label-as-title', 'arrow-on-label') || selected ? (<>
                            { this.is('label-as-title') ? (
                                typeof selected?.icon === 'string' ? <BraxIcon icon={selected.icon} /> : (selected?.icon ?? null)
                            ) : null }

                            <span>
                                { this.is('label-as-title')
                                    ? (
                                        <label>{ label } { required ? '*' : null }</label>
                                    )
                                : !this.is('arrow-on-label') && selected
                                    ? (
                                        <span>
                                            { this.is('no-description', 'complete', 'complete-value-as-title') && selected?.icon ? (
                                                typeof selected?.icon === 'string' ? <BraxIcon icon={selected.icon} /> : (selected?.icon ?? null)
                                            ) : null }

                                            <span>{ this.is('complete-value-as-title') ? selected.value as ReactNode : selected.title }</span>
                                        </span>
                                    )
                                    : null }

                                { this.is('label-as-title', 'arrow-on-label')
                                    ? (
                                        <span className={this.is('label-as-title') ? 'hint' : undefined}>
                                            { selected ? selected.title : placeholder }
                                        </span>
                                    )
                                : this.is('complete', 'complete-value-as-title') && selected?.description
                                    ? (
                                        <span>{ selected.description }</span>
                                    )
                                    : null
                                }
                            </span>
                        </>) : (
                            <span>{ placeholder }</span>
                        ) }

                        { !this.is('arrow-on-label') ? (
                            <BraxIcon icon="arrow" />
                        ) : null }
                    </span>
                </BraxDropdown>

                { hint ? (
                    <span className="hint">{ hint }</span>
                ) : null }
            </div>
        );
    }

}
